import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import type { SharedDiaryEntry } from '../../types';
import { useDateLocale } from '../../utils/dateLocale';
import { tripPhotoUrl } from '../../utils/tripPhotos';
import { EmptyNote } from './shared.ui';

/** ADR-0044: what the authors left shared, day by day, newest day first. */
export default function SharedDiary({ entries }: { entries: SharedDiaryEntry[] }) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();

  const byDay = useMemo(() => {
    const groups = new Map<string, SharedDiaryEntry[]>();
    for (const e of entries) groups.set(e.dayId, [...(groups.get(e.dayId) ?? []), e]);
    return [...groups.values()].sort((a, b) => b[0].day.dayNumber - a[0].day.dayNumber);
  }, [entries]);

  if (entries.length === 0) return <EmptyNote>{t('shared.noDiary')}</EmptyNote>;

  return (
    <div className="space-y-8">
      {byDay.map(group => (
        <section key={group[0].dayId} className="space-y-3">
          <header>
            <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400">
              {format(parseISO(group[0].day.date.slice(0, 10)), 'EEEE d MMMM', { locale: dateLocale })}
            </p>
            <h3 className="font-serif italic font-semibold text-xl text-slate-900 dark:text-slate-100">
              {t('shared.map.dayLabel', { n: group[0].day.dayNumber })}
            </h3>
          </header>
          {group.map(entry => (
            <article key={entry.id} className="card p-4 space-y-3">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">{entry.authorName}</p>
              <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200 whitespace-pre-line break-words">{entry.body}</p>
              {entry.photos.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                  {entry.photos.map(p => (
                    <a
                      key={p.id}
                      href={tripPhotoUrl(p.filename)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block aspect-square rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800"
                    >
                      <img src={tripPhotoUrl(p.thumbnailFilename ?? p.filename)} alt="" loading="lazy" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
            </article>
          ))}
        </section>
      ))}
    </div>
  );
}
